import {
  useArtwork,
  useArtworkForm,
  useDtfArtworks,
  useImagesWarmFastCache
} from "@design-lab/entities/artwork";
import { STANDARD_DTF_CATALOG_NUMBER } from "@design-lab/shared/constants/constants";
import { useAsyncEffect } from "@design-lab/shared/hooks/useAsyncEffect";
import { useNotification } from "@design-lab/shared/ui/notification";
import { useArtboard } from "@design-lab/widgets/artboard/model/useArtboard";
import { useMemo, useRef } from "react";
import { useSearchParams } from "react-router";
import {
  processArtworkData,
  setupArtworkForm,
  setupArtworkMachine,
  type ArtworkApiResponse
} from "../lib/artworkProcessor";

export function useEnhancementsStarted() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { error: notifyError } = useNotification();
  const hasProcessedRef = useRef(false);
  const { getMachineBySide } = useArtwork();
  const { form } = useArtworkForm();
  const { composition } = useArtboard();
  const { warmCache } = useImagesWarmFastCache();

  // Parse URL parameters with memoization
  const urlParams = useMemo(() => {
    const enhancementsStarted = searchParams.get("enhancements_started") === "true";
    const dtfChannelUuid = searchParams.get("dtf_channel_uuid");
    const artworkUrls = searchParams.get("artwork_urls");
    let urlsObj: Record<string, string> = {};
    if (artworkUrls) {
      try {
        urlsObj = JSON.parse(artworkUrls);
      } catch {
        try {
          urlsObj = JSON.parse(decodeURIComponent(artworkUrls));
        } catch {
          // Malformed artwork_urls -- degrade gracefully
        }
      }
    }
    const artworkId = Object.keys(urlsObj).pop() as string;
    const sideParam = searchParams.get("side") || "front";
    const side: "front" | "back" = sideParam.toLowerCase() === "back" ? "back" : "front";

    return {
      enhancementsStarted,
      dtfChannelUuid,
      artworkId,
      side,
      isValid: enhancementsStarted && dtfChannelUuid && artworkId
    };
  }, [searchParams]);

  // Make API call to fetch artwork details - only when valid params exist
  const {
    data: artworkDetails,
    isLoading,
    error
  } = useDtfArtworks(
    urlParams.isValid
      ? {
          apiType: "details" as const,
          catalogNumber: STANDARD_DTF_CATALOG_NUMBER,
          dtfChannelUuid: urlParams.dtfChannelUuid || "",
          artworkIds: urlParams.artworkId ? [urlParams.artworkId] : [],
          enhancementsStarted: true
        }
      : {}
  );

  // Handle API errors
  useAsyncEffect(async () => {
    if (error) {
      notifyError({
        title: "Failed to Load Artwork",
        description: "Unable to fetch artwork details. Please try again."
      });
    }
  }, [error, notifyError]);

  useAsyncEffect(async () => {
    if (!urlParams.isValid || !artworkDetails || hasProcessedRef.current) return;

    const artwork = (artworkDetails as any).artworks?.[0] as ArtworkApiResponse;
    if (!artwork) {
      notifyError({
        title: "Artwork Not Found",
        description: "The requested artwork could not be found."
      });
      return;
    }

    // Check for metadata error
    const metadata = artwork?.ordered?.metadata || {};
    if (metadata?.error) {
      notifyError({
        title: "Artwork Error",
        description: String(metadata.error)
      });
      return;
    }

    try {
      hasProcessedRef.current = true;

      // Process artwork data using shared utility
      const processedArtwork = processArtworkData(artwork);
      const machine = getMachineBySide(urlParams.side);

      // Warm image for sizing calculations
      const imageUrl = processedArtwork.originalImageUrl || processedArtwork.url;
      const [cached] = await warmCache([imageUrl]);

      // Setup form using shared utility
      setupArtworkForm(form, {
        artwork: processedArtwork,
        side: urlParams.side,
        cachedImage: cached,
        composition
      });

      // Setup machine state using shared utility
      setupArtworkMachine(machine, processedArtwork, artwork.currentEnhancementState);

      // Clean up query parameters after successful processing
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("enhancements_started");
      newParams.delete("dtf_channel_uuid");
      newParams.delete("artwork_urls");
      newParams.delete("side");
      newParams.delete("file_content_type");
      newParams.delete("catalog_number");
      newParams.delete("ac");
      newParams.delete("design-library");
      newParams.delete("canva");
      setSearchParams(newParams, { replace: true });
    } catch (setupError) {
      console.error("Error setting up artwork:", setupError);
      window.Honeybadger?.notify("Error setting up artwork", {
        params: {
          error: setupError instanceof Error ? setupError.message : String(setupError),
          error_object: setupError instanceof Error ? setupError.toString() : String(setupError),
          stack_trace: setupError instanceof Error ? setupError.stack : null,
          hook: "useEnhancementsStarted",
          artworkId: artwork?.id
        }
      });
      hasProcessedRef.current = false; // Reset on error to allow retry
      notifyError({
        title: "Setup Error",
        description: "Failed to initialize artwork. Please try refreshing the page."
      });
    }
  }, [
    urlParams.isValid,
    artworkDetails,
    form,
    composition,
    warmCache,
    getMachineBySide,
    urlParams.side,
    notifyError,
    searchParams,
    setSearchParams
  ]);

  // Return the detection state and loading info
  return {
    ...urlParams,
    isLoading,
    error
  };
}
